import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, MapPin, Phone, Mail } from "lucide-react";

const serviceOptions = [
  "Diseño Arquitectónico",
  "Construcción",
  "Gestoría & Administración",
  "Naves Industriales",
  "Casa Habitación",
  "Terracería",
  "Render 3D",
  "Instalaciones",
  "Remodelación",
  "Casas Inteligentes",
  "Otro",
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Por favor completa nombre y email");
      return;
    }
    setSending(true);
    try {
      await api.createLead(form);
      toast.success("Mensaje enviado. Nos pondremos en contacto pronto.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#F5F5F7] relative noise-overlay"
      data-testid="contact-section"
    >
      <div className="max-w-[1800px] mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-20">
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
              Contacto
            </p>
          </div>
          <div className="md:col-span-10">
            <h2
              className="text-3xl md:text-5xl lg:text-6xl font-normal text-neutral-900 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Hablemos de tu proyecto
            </h2>
          </div>
        </div>

        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-lg text-neutral-500 font-light leading-relaxed mb-12">
              Cuéntanos sobre tu visión y en <span className="text-neutral-800 font-normal">Merza Soluciones</span> la 
              haremos realidad. Cada proyecto es único y merece una atención personalizada.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} strokeWidth={1.5} className="text-neutral-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Teléfono</p>
                  <p className="text-neutral-900 font-medium">(55) 4442-1964</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} strokeWidth={1.5} className="text-neutral-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Email</p>
                  <p className="text-neutral-900 font-medium">raziel.zavala@merzasoluciones.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} strokeWidth={1.5} className="text-neutral-600" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Ubicación</p>
                  <p className="text-neutral-900 font-medium">Ciudad de México, México</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={handleSubmit}
            className="space-y-8"
            data-testid="contact-form"
          >
            <div>
              <input
                data-testid="contact-name-input"
                type="text"
                placeholder="Nombre completo"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-14 bg-transparent border-b border-neutral-300 focus:border-black text-lg text-neutral-900 outline-none transition-colors placeholder:text-neutral-300 font-light"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input
                data-testid="contact-email-input"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full h-14 bg-transparent border-b border-neutral-300 focus:border-black text-lg text-neutral-900 outline-none transition-colors placeholder:text-neutral-300 font-light"
              />
              <input
                data-testid="contact-phone-input"
                type="tel"
                placeholder="Teléfono"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full h-14 bg-transparent border-b border-neutral-300 focus:border-black text-lg text-neutral-900 outline-none transition-colors placeholder:text-neutral-300 font-light"
              />
            </div>

            <div>
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full h-14 bg-transparent border-b border-neutral-300 focus:border-black text-lg text-neutral-900 outline-none transition-colors font-light appearance-none cursor-pointer"
              >
                <option value="" className="text-neutral-300">Servicio de interés</option>
                {serviceOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <textarea
                data-testid="contact-message-input"
                placeholder="Cuéntanos sobre tu proyecto..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full bg-transparent border-b border-neutral-300 focus:border-black text-lg text-neutral-900 outline-none transition-colors placeholder:text-neutral-300 font-light resize-none"
              />
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 bg-black text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {sending ? "Enviando..." : "Enviar mensaje"}
              <Send size={14} strokeWidth={1.5} />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
